import React, { useMemo } from "react";
import {
    Box,
    Paper,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
} from "@mui/material";


const headerSx = { "& .MuiTableCell-root": { color: "primary.dark", fontSize: "0.85rem" } };

const AircraftUtilization = ({ aircraftUtilization = {}, availableByRank = [] }) => {
    const rows = useMemo(() => Object.values(aircraftUtilization || {}), [aircraftUtilization]);

    const rankStats = useMemo(() => {
        const availableMap = new Map(availableByRank.map((r) => [r.rank, r.available]));
        const priorityMap  = new Map(availableByRank.map((r) => [r.rank, r.rankPriority]));

        const totals = {};
        for (const aircraft of rows) {
            for (const c of aircraft.crewComplement || []) {
                const rank = c.rankCode;
                const quantity  = c.quantity ?? 0;
                if (!rank || !quantity) continue;
                totals[rank] = (totals[rank] ?? 0) + aircraft.blockHours * quantity;
            }
        }

        return Object.entries(totals)
            .map(([rank, crewBH]) => {
                const available = availableMap.get(rank) ?? 0;
                return {
                    rank,
                    available,
                    crewBH,
                    avgBH: available > 0 ? crewBH / available : 0,
                    rankPriority: priorityMap.get(rank) ?? 999,
                };
            })
            .sort((a, b) => a.rankPriority - b.rankPriority);
    }, [rows, availableByRank]);

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            <Paper sx={{ px: 2, pt: 1 }}>
                <Table size="small">
                    <TableHead sx={headerSx}>
                        <TableRow>
                            <TableCell>Aircraft</TableCell>
                            <TableCell align="right">Flights</TableCell>
                            <TableCell align="right">Block Hours</TableCell>
                            <TableCell>Crew Set</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, idx) => (
                            <TableRow key={idx}>
                                <TableCell>{row?.flightReg || row?.acType}</TableCell>
                                <TableCell align="right">{row?.flights}</TableCell>
                                <TableCell align="right">{row.blockHours?.toFixed(1)}</TableCell>
                                <TableCell>
                                    {row?.crewComplement?.length
                                        ? row.crewComplement
                                            .map((c) => `${c?.rankCode}: ${c?.crewSet ?? 0}`)
                                            .join(", ")
                                        : "-"}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </Paper>

            {rankStats.length > 0 && (
                <Paper sx={{ px: 2, pt: 1 }}>
                    <Table size="small">
                        <TableHead sx={headerSx}>
                            <TableRow>
                                <TableCell>Rank</TableCell>
                                <TableCell align="right">Available</TableCell>
                                <TableCell align="right">Crew BH</TableCell>
                                <TableCell align="right">BH / Crew</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rankStats.map((r) => (
                                <TableRow key={r.rank}>
                                    <TableCell>{r.rank}</TableCell>
                                    <TableCell align="right">{r.available.toFixed(1)}</TableCell>
                                    <TableCell align="right">{r.crewBH.toFixed(1)}</TableCell>
                                    <TableCell align="right">{r.avgBH.toFixed(1)}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Paper>
            )}
        </Box>
    );
};

export default AircraftUtilization;
